import { API_BASE_URL } from '../../../../config';

/**
 * Discriminator for fill-in document PDF preview endpoints.
 * Add new kinds when other documents expose a preview API.
 */
export type FillInPdfPreviewKind =
  | 'worker-registry'
  | 'hire-health-survey'
  | 'bank-account-details'
  | 'tax-declaration'
  | 'work-dust-history'
  | 'pre-employment-education'
  | 'site-arrival-confirmation'
  | 'career-up-application'
  | 'health-info-zuido'
  | 'employment-contract'
  | 'pledge'
  | 'password-handling'
  | 'personal-seal'
  | 'previous-employment-inquiry';

export interface FillInPdfPreviewRequest {
  kind: FillInPdfPreviewKind;
  /** Localized checklist / document name (for UI only) */
  documentTitle: string;
  formCode: string;
  email: string;
  invitationId?: string;
  taxYear?: number;
  /** FF009 post-consent preview: fill name, date, signature on page 3 only. */
  filledPreview?: boolean;
  /** FF009: which document (1=registration, 2=system consent, 3=personal info). */
  page?: number;
  /** FF011 form 4 — single-page work history confirmation preview (page 3). */
  workHistoryFormOnly?: boolean;
  /** Post-agree preview (consent date; signature when applicable). */
  signedPreview?: boolean;
}

export function buildFillInPdfPreviewUrl(req: FillInPdfPreviewRequest): string | null {
  const base = API_BASE_URL?.trim();
  if (!base) return null;
  const formCode = req.formCode.trim();
  if (!formCode) return null;

  switch (req.kind) {
    case 'worker-registry': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/worker-registry/preview?${params.toString()}`;
    }
    case 'hire-health-survey': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/hire-health-survey/preview?${params.toString()}`;
    }
    case 'bank-account-details': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email });
      return `${base}/api/bank-account-details/preview?${params.toString()}`;
    }
    case 'tax-declaration': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode });
      if (req.taxYear != null) {
        params.set('taxYear', String(req.taxYear));
      }
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/tax-declaration/preview?${params.toString()}`;
    }
    case 'work-dust-history': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/work-dust-history/preview?${params.toString()}`;
    }
    case 'pre-employment-education': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/pre-employment-checklist/preview?${params.toString()}`;
    }
    case 'site-arrival-confirmation': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode: req.formCode.trim() });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      return `${base}/api/pre-employment-checklist/preview?${params.toString()}`;
    }
    case 'career-up-application': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({ email, formCode: req.formCode.trim() });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      const page = req.page != null && req.page >= 1 && req.page <= 3 ? req.page : 1;
      params.set('page', String(page));
      if (req.filledPreview) params.set('filled', 'true');
      return `${base}/api/career-up-application/preview?${params.toString()}`;
    }
    case 'health-info-zuido': {
      const params = new URLSearchParams({ formCode: req.formCode.trim() });
      const page =
        req.workHistoryFormOnly === true
          ? 3
          : req.page != null && req.page >= 1 && req.page <= 3
            ? req.page
            : 1;
      params.set('page', String(page));
      if (req.filledPreview || req.workHistoryFormOnly) {
        const email = req.email.trim();
        if (!email) return null;
        params.set('email', email);
        if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
        params.set('filled', 'true');
      }
      return `${base}/api/health-info-zuido/preview?${params.toString()}`;
    }
    case 'employment-contract': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({
        email,
        formCode: req.formCode.trim() || 'FF002',
      });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      if (req.signedPreview) params.set('signed', 'true');
      return `${base}/api/employment-contract/preview?${params.toString()}`;
    }
    case 'pledge': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({
        email,
        formCode: req.formCode.trim() || 'FF004',
      });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      if (req.signedPreview) params.set('signed', 'true');
      return `${base}/api/pledge/preview?${params.toString()}`;
    }
    case 'password-handling': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({
        email,
        formCode: req.formCode.trim() || 'FF010',
      });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      if (req.signedPreview) params.set('signed', 'true');
      return `${base}/api/password-handling/preview?${params.toString()}`;
    }
    case 'personal-seal': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({
        email,
        formCode: req.formCode.trim() || 'FF013',
      });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      if (req.signedPreview) params.set('signed', 'true');
      return `${base}/api/personal-seal/preview?${params.toString()}`;
    }
    case 'previous-employment-inquiry': {
      const email = req.email.trim();
      if (!email) return null;
      const params = new URLSearchParams({
        email,
        formCode: req.formCode.trim() || 'FF015',
      });
      if (req.invitationId?.trim()) params.set('invitationId', req.invitationId.trim());
      if (req.signedPreview) params.set('signed', 'true');
      return `${base}/api/previous-employment-inquiry/preview?${params.toString()}`;
    }
    default:
      return null;
  }
}

export async function fetchFillInPdfPreviewBlob(req: FillInPdfPreviewRequest): Promise<Blob> {
  const url = buildFillInPdfPreviewUrl(req);
  if (!url) throw new Error('preview url unavailable');
  const res = await fetch(url);
  if (!res.ok) throw new Error('preview failed');
  return res.blob();
}
